import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;

const STATUS_META = {
  aktif:       { label: "Aktif",        bg: "#EAF3DE", color: "#3B6D11" },
  selesai:     { label: "Selesai",      bg: "#E1F5EE", color: "#0F6E56" },
  lambat:      { label: "Lambat",       bg: "#FAEEDA", color: "#854F0B" },
  tidak_aktif: { label: "Tidak aktif",  bg: "#FCEBEB", color: "#A32D2D" },
  belum_mulai: { label: "Belum mulai",  bg: "#F1EFE8", color: "#5F5E5A" },
};

function Avatar({ name, picture, size = 32 }) {
  const initials = name?.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
  if (picture) return (
    <img src={toAbsUrl(picture)} alt=""
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
  );
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: "#EEEDFE",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 500, color: "#534AB7", flexShrink: 0,
    }}>{initials}</div>
  );
}

function MetricCard({ icon, label, value, sub, warn }) {
  return (
    <div style={{ background: "var(--color-background-secondary)", borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 4 }}>
        <i className={`ti ${icon}`} aria-hidden="true" style={{ marginRight: 4 }} />{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 500, color: warn ? "#BA7517" : "var(--color-text-primary)" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function getWeekLabel(offset) {
  const now = new Date();
  const day = now.getDay() || 7;
  const mon = new Date(now); mon.setDate(now.getDate() - day + 1 + offset * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const M = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
  const fmt = d => `${d.getDate()} ${M[d.getMonth()]}`;
  return `${fmt(mon)} – ${fmt(sun)}`;
}

function getMonthLabel(offset) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const M = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
  return `${M[d.getMonth()]} ${d.getFullYear()}`;
}

function ActivityChart({ data, mode, offset }) {
  const canvasRef = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const raw = mode === "week" ? data.activity_weekly : data.activity_monthly;
    const maxOffset = mode === "week" ? 11 : 5;
    const idx = Math.min(Math.abs(offset), maxOffset);
    const dayData = raw[idx] || [];

    // Weekly: 7 hari (Sen-Min), Monthly: per hari dalam bulan
    const labels = mode === "week"
      ? ["Sen","Sel","Rab","Kam","Jum","Sab","Min"]
      : dayData.map((_, i) => i + 1 === 1 || (i + 1) % 5 === 0 ? `${i + 1}` : "");

    const materi = dayData.map(d => d.materi || 0);
    const tugas  = dayData.map(d => d.tugas  || 0);

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.parentElement.clientWidth;
    const H = 180;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + "px"; canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const pad = { top: 20, right: 16, bottom: 34, left: 34 };
    const w = W - pad.left - pad.right;
    const h = H - pad.top - pad.bottom;
    const maxVal = Math.max(...materi, ...tugas, 1);
    const yMax = Math.ceil(maxVal) || 1;

    const isDark = document.documentElement.classList.contains("dark") ||
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const gridC = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)";
    const textC = isDark ? "#7A7088" : "#8B8794";
    const textP = isDark ? "#F0EDF7" : "#1A1422";
    const cardBg = isDark ? "#1C1728" : "#ffffff";

    ctx.clearRect(0, 0, W, H);

    for (let i = 0; i <= 4; i++) {
      const y = pad.top + h - (i / 4) * h;
      ctx.strokeStyle = gridC; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + w, y); ctx.stroke();
      ctx.fillStyle = textC; ctx.font = "10px -apple-system,sans-serif"; ctx.textAlign = "right";
      ctx.fillText(Math.round((i / 4) * yMax), pad.left - 6, y + 3.5);
    }

    const n = materi.length || 1;
    ctx.fillStyle = textC; ctx.font = "10px -apple-system,sans-serif"; ctx.textAlign = "center";
    for (let i = 0; i < n; i++) {
      if (labels[i]) ctx.fillText(labels[i], pad.left + (i / (n - 1)) * w, H - 7);
    }

    const drawLine = (vals, color, fillColor) => {
      const pts = vals.map((v, i) => ({ x: pad.left + (i / (n - 1)) * w, y: pad.top + h - (v / yMax) * h }));
      ctx.beginPath(); ctx.moveTo(pts[0].x, pad.top + h); ctx.lineTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        const mx = (pts[i-1].x + pts[i].x) / 2;
        ctx.bezierCurveTo(mx, pts[i-1].y, mx, pts[i].y, pts[i].x, pts[i].y);
      }
      ctx.lineTo(pts[pts.length-1].x, pad.top + h); ctx.closePath();
      ctx.fillStyle = fillColor; ctx.fill();

      ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        const mx = (pts[i-1].x + pts[i].x) / 2;
        ctx.bezierCurveTo(mx, pts[i-1].y, mx, pts[i].y, pts[i].x, pts[i].y);
      }
      ctx.strokeStyle = color; ctx.lineWidth = 2.5; ctx.lineJoin = "round"; ctx.stroke();

      pts.forEach(p => {
        ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = color; ctx.fill();
        ctx.strokeStyle = cardBg; ctx.lineWidth = 2; ctx.stroke();
      });

      ctx.fillStyle = textP; ctx.font = "bold 11px -apple-system,sans-serif"; ctx.textAlign = "center";
      pts.forEach((p, i) => { if (vals[i] > 0) ctx.fillText(vals[i], p.x, p.y - 10); });
    };

    drawLine(materi, "#6B21A8", "rgba(107,33,168,0.07)");
    drawLine(tugas,  "#FF8C00", "rgba(255,140,0,0.07)");
  }, [data, mode, offset]);

  useEffect(() => {
    draw();
    const ro = new ResizeObserver(draw);
    if (canvasRef.current?.parentElement) ro.observe(canvasRef.current.parentElement);
    return () => ro.disconnect();
  }, [draw]);

  return <canvas ref={canvasRef} />;
}

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actMode, setActMode] = useState("week");
  const [actOffset, setActOffset] = useState(0);

  useEffect(() => {
    api.get("/admin/analytics").then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#8B8794] text-sm py-10 text-center">Memuat analitik...</div>;
  if (!data) return <div className="text-[#8B8794] text-sm py-10 text-center">Gagal memuat data.</div>;

  const card = { background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 12, padding: 16 };
  const sectionTitle = { fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 12 };

  const maxOffset = actMode === "week" ? -11 : -5;
  const rangeLabel = actMode === "week" ? getWeekLabel(actOffset) : getMonthLabel(actOffset);

  const navBtn = (dir) => {
    const next = actOffset + dir;
    if (next > 0 || next < maxOffset) return;
    setActOffset(next);
  };

  const progressColor = (pct) => pct >= 80 ? "#1D9E75" : pct >= 50 ? "#378ADD" : pct >= 20 ? "#EF9F27" : "#E24B4A";
  const gradeColor = (g) => g >= 80 ? "#1D9E75" : g >= 60 ? "#378ADD" : "#F59E0B";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Metric cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
        <MetricCard icon="ti-users" label="Total siswa" value={data.total_students} sub={`${data.active_students} aktif bulan ini`} />
        <MetricCard icon="ti-chart-line" label="Avg progress" value={`${data.avg_progress}%`} />
        <MetricCard icon="ti-star" label="Avg nilai" value={data.avg_grade || "—"} sub="dari 100" />
        <MetricCard icon="ti-file-check" label="Pending penilaian" value={data.pending_grading} warn={data.pending_grading > 0} />
      </div>

      {/* Aktivitas Belajar chart */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div style={sectionTitle}>Aktivitas Belajar</div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <select
              value={actMode}
              onChange={e => { setActMode(e.target.value); setActOffset(0); }}
              style={{
                appearance: "none", WebkitAppearance: "none",
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-border-tertiary)", borderRadius: 8,
                padding: "5px 26px 5px 10px", fontSize: 12, fontWeight: 500,
                color: "var(--color-text-primary)", cursor: "pointer", outline: "none",
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%238B8794' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat", backgroundPosition: "right 7px center",
              }}
            >
              <option value="week">Mingguan</option>
              <option value="month">Bulanan</option>
            </select>
            <button onClick={() => navBtn(-1)} disabled={actOffset <= maxOffset}
              style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid var(--color-border-tertiary)", background: "var(--color-background-secondary)", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", opacity: actOffset <= maxOffset ? 0.4 : 1 }}>
              ‹
            </button>
            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)", minWidth: 150, textAlign: "center" }}>{rangeLabel}</span>
            <button onClick={() => navBtn(1)} disabled={actOffset >= 0}
              style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid var(--color-border-tertiary)", background: "var(--color-background-secondary)", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", opacity: actOffset >= 0 ? 0.4 : 1 }}>
              ›
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
          {[["#6B21A8","Materi selesai"],["#FF8C00","Tugas dikumpulkan"]].map(([c,l]) => (
            <span key={l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--color-text-secondary)" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: c, flexShrink: 0 }} />{l}
            </span>
          ))}
        </div>
        <ActivityChart data={data} mode={actMode} offset={actOffset} />
      </div>

      {/* Top 3 + Perlu Perhatian */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>

        {/* Top 3 Siswa */}
        <div style={card}>
          <div style={sectionTitle}>Top 3 Siswa</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(data.top_performers?.length ? data.top_performers : data.student_rows).slice(0, 3).map((r, i) => {
              const score = r.combined_score ?? r.avg_progress;
              return (
                <div key={r.user_id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 22, textAlign: "center", fontSize: 16, flexShrink: 0 }}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}
                  </div>
                  <Avatar name={r.name} picture={r.picture} size={34} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 5 }}>{r.name}</div>
                    <div style={{ height: 5, borderRadius: 3, background: "var(--color-background-secondary)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${score}%`, background: progressColor(score), borderRadius: 3 }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexShrink: 0, textAlign: "right" }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)", lineHeight: 1 }}>{r.total_completed_lessons ?? "—"}</div>
                      <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 2 }}>materi</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)", lineHeight: 1 }}>{r.total_submissions ?? "—"}</div>
                      <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 2 }}>tugas</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: r.avg_grade != null ? gradeColor(r.avg_grade) : "var(--color-text-secondary)", lineHeight: 1 }}>
                        {r.avg_grade != null ? r.avg_grade : "—"}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 2 }}>avg nilai</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: progressColor(score), lineHeight: 1 }}>{score}%</div>
                      <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 2 }}>progress</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {data.student_rows.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13, padding: "20px 0" }}>
                Belum ada data siswa.
              </div>
            )}
          </div>
        </div>

        {/* Perlu Perhatian */}
        {data.need_attention?.length > 0 && (
          <div style={card}>
            <div style={sectionTitle}>Perlu perhatian</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {data.need_attention?.map(r => {
                const meta = STATUS_META[r.status] || STATUS_META.belum_mulai;
                return (
                  <div key={r.user_id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={r.name} picture={r.picture} size={34} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
                        {r.status === "tidak_aktif" ? `Tidak aktif ${r.days_inactive} hari` :
                         r.status === "lambat" ? `Progress lambat — ${r.avg_progress}%` :
                         "Belum mulai belajar"}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: meta.bg, color: meta.color, fontWeight: 500, flexShrink: 0 }}>
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
